import * as React from 'react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

import { useUser } from '@/hooks/v2/use-user';

import { getVisibleTourSteps } from './tour-steps';

const SEEN_STORAGE_PREFIX = 'hoaone:product-tour:seen:v1:';

interface ProductTourContextValue {
  /** Start (or restart) the guided tour. */
  startTour: () => void;
}

const ProductTourContext = React.createContext<ProductTourContextValue | undefined>(undefined);

export function useProductTour(): ProductTourContextValue {
  const context = React.useContext(ProductTourContext);
  if (!context) {
    throw new Error('useProductTour must be used within a ProductTourProvider');
  }
  return context;
}

function seenKey(userId?: string | number): string {
  return `${SEEN_STORAGE_PREFIX}${userId ?? 'anon'}`;
}

export function ProductTourProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { user, isLoading } = useUser();
  const driverRef = React.useRef<Driver | null>(null);
  const userId = user?.id;

  const markSeen = React.useCallback((): void => {
    try {
      window.localStorage.setItem(seenKey(userId), '1');
    } catch {
      // localStorage may be unavailable (private mode) — non-fatal.
    }
  }, [userId]);

  const startTour = React.useCallback((): void => {
    // The tour targets sidebar nav items which are only visible on desktop.
    if (!window.matchMedia('(min-width: 1200px)').matches) return;

    const steps = getVisibleTourSteps();
    if (steps.length === 0) return;

    driverRef.current?.destroy();
    const instance = driver({
      showProgress: true,
      allowClose: true,
      animate: true,
      smoothScroll: false,
      overlayColor: '#0a1f44',
      overlayOpacity: 0.6,
      stagePadding: 6,
      stageRadius: 8,
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      popoverClass: 'hoaone-tour',
      steps,
      onHighlightStarted: (element) => {
        if (!(element instanceof HTMLElement)) return;
        // Scroll the sidebar's own nav container so the target item is in view
        // without triggering a page scroll (which would visually bounce on a
        // fixed sidebar).
        const scrollContainer = element.closest('nav');
        if (scrollContainer) {
          const elTop = element.offsetTop;
          const elBottom = elTop + element.offsetHeight;
          const containerTop = scrollContainer.scrollTop;
          const containerBottom = containerTop + scrollContainer.clientHeight;
          if (elTop < containerTop) {
            scrollContainer.scrollTop = elTop - 8;
          } else if (elBottom > containerBottom) {
            scrollContainer.scrollTop = elBottom - scrollContainer.clientHeight + 8;
          }
        }
      },
      onDestroyed: () => {
        markSeen();
      },
    });
    driverRef.current = instance;
    instance.drive();
  }, [markSeen]);

  // Auto-start once per user on first visit (desktop only — the sidebar the
  // tour points at is hidden on small screens).
  React.useEffect(() => {
    if (isLoading || !userId) return;
    if (typeof window === 'undefined') return;

    let alreadySeen = false;
    try {
      alreadySeen = window.localStorage.getItem(seenKey(userId)) === '1';
    } catch {
      alreadySeen = true; // If we can't read the flag, don't nag the user.
    }
    if (alreadySeen) return;

    const isDesktop = window.matchMedia('(min-width: 1200px)').matches;
    if (!isDesktop) return;

    // Wait a beat so the sidebar nav has rendered before we target it.
    const timer = window.setTimeout(() => {
      startTour();
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [isLoading, userId, startTour]);

  React.useEffect(() => {
    return () => {
      driverRef.current?.destroy();
    };
  }, []);

  const value = React.useMemo<ProductTourContextValue>(() => ({ startTour }), [startTour]);

  return (
    <ProductTourContext.Provider value={value}>
      <GlobalStyles
        styles={{
          '.driver-popover.hoaone-tour': {
            borderRadius: '12px',
            padding: '20px',
          },
          '.driver-popover.hoaone-tour .driver-popover-title': {
            fontSize: '1.05rem',
            fontWeight: 700,
          },
          '.driver-popover.hoaone-tour .driver-popover-description': {
            fontSize: '0.9rem',
            lineHeight: 1.5,
          },
          '.driver-popover.hoaone-tour .driver-popover-next-btn': {
            backgroundColor: '#0a1f44',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            textShadow: 'none',
            padding: '7px 14px',
            fontWeight: 600,
          },
          '.driver-popover.hoaone-tour .driver-popover-next-btn:hover': {
            backgroundColor: '#13315c',
          },
          '.driver-popover.hoaone-tour .driver-popover-prev-btn': {
            borderRadius: '8px',
            textShadow: 'none',
          },
        }}
      />
      {children}
    </ProductTourContext.Provider>
  );
}
